import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Open/close state for `MenuPhotoSwipe`, shared by the restaurant page, the menu page and the city-page cards. The backdrop stays mounted until the 300ms close transition finishes, and a quick close→reopen (or an unmount) cancels that timer so it can never drop the backdrop while the viewer is open.
 */
export default function useLightbox() {
  const [isOpen, setIsOpen] = useState(false);
  const [showBackground, setShowBackground] = useState(false);
  const [index, setIndex] = useState(0);
  const backgroundTimeout = useRef<ReturnType<typeof setTimeout>>();

  const clearBackgroundTimeout = useCallback(() => {
    clearTimeout(backgroundTimeout.current);
    backgroundTimeout.current = undefined;
  }, []);

  useEffect(() => () => clearBackgroundTimeout(), [clearBackgroundTimeout]);

  const open = useCallback(
    (startIndex = 0) => {
      clearBackgroundTimeout();
      setIndex(startIndex);
      setShowBackground(true);
      setIsOpen(true);
    },
    [clearBackgroundTimeout]
  );

  const close = useCallback(() => {
    setIsOpen(false);
    clearBackgroundTimeout();
    backgroundTimeout.current = setTimeout(() => setShowBackground(false), 300);
  }, [clearBackgroundTimeout]);

  const onChange = useCallback((newIndex: number) => {
    setIndex((current) => (current === newIndex ? current : newIndex));
  }, []);

  return { isOpen, index, showBackground, open, close, onChange };
}
